import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, Clock, Calendar } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "../../lib/api";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function HeatmapCell({ value, max }) {
  if (!max) return <div className="w-full h-full rounded-sm bg-muted/20" />;
  const intensity = value / max;
  const alpha = Math.max(0.08, intensity);
  return (
    <div
      className="w-full h-full rounded-sm transition-opacity"
      style={{ backgroundColor: `hsl(var(--primary) / ${alpha})` }}
      title={`${value.toFixed(0)}€`}
    />
  );
}

export function TimeTab({ restaurantId, startDate, endDate, closedDays = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/analytics/time?${new URLSearchParams({ ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) })}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [restaurantId, startDate, endDate]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const { heatmap, hourly, dow, best_hour, best_dow_label } = data;

  // Build heatmap matrix
  const heatMax = Math.max(...heatmap.map(h => h.value), 1);
  const matrix = {};
  heatmap.forEach(h => { matrix[`${h.day}-${h.hour}`] = h.value; });

  // Best hours (top 3)
  const sortedHours = [...hourly].sort((a, b) => b.ventas - a.ventas);
  const topHours = sortedHours.slice(0, 3);
  const maxHourly = Math.max(...hourly.map(h => h.ventas), 1);

  return (
    <div className="space-y-6">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-3">
        {best_hour !== null && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
            <Clock className="w-4 h-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Mejor hora</p>
              <p className="text-sm font-bold">{best_hour}:00 – {best_hour + 1}:00</p>
            </div>
          </div>
        )}
        {best_dow_label && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
            <Calendar className="w-4 h-4 text-green-600" />
            <div>
              <p className="text-xs text-muted-foreground">Mejor día</p>
              <p className="text-sm font-bold">{best_dow_label}</p>
            </div>
          </div>
        )}
        {topHours.map((h, i) => (
          <Badge key={h.hour} variant="outline" className="text-xs px-3 py-1.5">
            #{i + 1} franja: {h.hour}:00h ({h.ventas.toFixed(0)}€)
          </Badge>
        ))}
      </div>

      {/* Heatmap */}
      <Card data-testid="time-heatmap">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Mapa de calor: Hora × Día de la semana</CardTitle>
          <p className="text-xs text-muted-foreground">
            Intensidad = volumen de ventas
            {closedDays.length > 0 && (
              <span className="ml-2 text-destructive/80">· Días rayados = cerrado (excluidos)</span>
            )}
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 560 }}>
              {/* Hour labels top */}
              <div className="flex mb-1">
                <div className="w-10 flex-shrink-0" />
                {HOURS.filter(h => h % 2 === 0).map(h => (
                  <div key={h} className="flex-1 text-center text-[9px] text-muted-foreground" style={{ minWidth: 20 }}>
                    {h}h
                  </div>
                ))}
              </div>
              {DAYS.map((dayLabel, dayIdx) => {
                const isClosed = closedDays.includes(dayIdx);
                return (
                  <div key={dayIdx} className={`flex items-center mb-1 ${isClosed ? "opacity-30" : ""}`} style={{ height: 26 }}>
                    <div className={`w-10 flex-shrink-0 text-[10px] text-right pr-2 ${isClosed ? "line-through text-destructive" : "text-muted-foreground"}`}>
                      {dayLabel}
                    </div>
                    {HOURS.map(h => (
                      <div key={h} className="flex-1 h-5 mx-0.5" style={{ minWidth: 18 }}>
                        {isClosed
                          ? <div className="w-full h-full rounded-sm bg-muted/10 border border-dashed border-muted-foreground/20" />
                          : <HeatmapCell value={matrix[`${dayIdx}-${h}`] || 0} max={heatMax} />
                        }
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hourly bar chart */}
      <Card data-testid="time-hourly-chart">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Ventas por franja horaria</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourly} barSize={12}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={h => `${h}h`} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} />
              <Tooltip formatter={v => [`${v}€`, "Ventas"]} labelFormatter={l => `${l}:00h`} />
              <Bar dataKey="ventas" radius={[3, 3, 0, 0]}>
                {hourly.map((entry, i) => (
                  <Cell key={i} fill={entry.ventas >= (maxHourly * 0.7) ? "hsl(var(--primary))" : "hsl(var(--primary) / 0.4)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Day of week */}
      <Card data-testid="time-dow-chart">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Ventas por día de la semana</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={dow} barSize={24}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} />
              <Tooltip formatter={v => [`${v}€`, "Ventas"]} labelFormatter={l => closedDays.includes(["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].indexOf(l)) ? `${l} (cerrado)` : l} />
              <Bar dataKey="ventas" radius={[4, 4, 0, 0]}>
                {dow.map((entry, i) => {
                  const isClosed = closedDays.includes(i);
                  const maxVal = Math.max(...dow.map(d => d.ventas));
                  return (
                    <Cell
                      key={i}
                      fill={isClosed
                        ? "hsl(var(--muted-foreground) / 0.2)"
                        : entry.ventas === maxVal
                          ? "hsl(var(--primary))"
                          : "hsl(var(--primary) / 0.45)"}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {closedDays.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Barras en gris = días sin actividad (excluidos del cálculo)
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
